/**
 * Tur Bilgi Formu: ekran ile CRM API'si arasındaki sözleşme (taslak, crm-engineer yazdı).
 *
 * Bütün adresler `/api/v1/tour-factsheet` altında. Fiyat ve içerik sitenin kendi motorundan
 * (v3) gelir; ekran fiyat hesaplamaz, yalnız gösterir. İç veri (maliyet, komisyon, net fiyat,
 * marj, tedarikçi, acente fiyatı) bu tiplerde bilerek YOKTUR ve eklenmemelidir.
 *
 *   GET  /tours?q=<tur no | ad | slug>            -> TurAramaCevabi
 *   GET  /tours/:turNo                             -> TurBilgisi
 *   POST /quote                                    -> FiyatCevabi
 *   POST /pdf                                      -> PdfCevabi
 *   POST /pdf/:paylasimId/email                    -> GonderimCevabi
 *   POST /pdf/:paylasimId/whatsapp                 -> GonderimCevabi
 *   GET  /status                                   -> KanalDurumu
 */

export type Dil = 'tr' | 'en';
export type ParaBirimi = 'USD' | 'EUR' | 'AED';
export type PdfTipi = 'BROCHURE' | 'VARIANT_QUOTE';

/** PUBLISHED: satışta. PRE_ORDER: pasif ya da tarihi geçmiş, ön talep formu açık; PDF üretilmez. */
export type TurDurumu = 'PUBLISHED' | 'PRE_ORDER';

/** Tur numarası: 6 haneli rakam, kalıcı, kalkış şehri + havayolu + gece sayısına göre benzersiz. */
export type TurNo = string;

export interface TurOzeti {
  readonly turNo: TurNo;
  readonly slug: string;
  readonly ad: string;
  readonly ulke: string | null;
  readonly gunSayisi: number;
  readonly geceSayisi: number;
  readonly havayolu: string | null;
  readonly kalkisSehri: string | null;
  readonly durum: TurDurumu;
  /** Sitedeki "başlangıç fiyatı kişi başı"; PRE_ORDER turda null. */
  readonly baslangicFiyati: Para | null;
}

export interface TurAramaCevabi {
  readonly sonuclar: readonly TurOzeti[];
}

export interface Para {
  /** Küçük birim (cent, fils). */
  readonly tutarMinor: number;
  readonly paraBirimi: ParaBirimi;
}

export interface OtelSinifi {
  /** v3'te sınıf kimliği yıldız değil `position`. Adreste `?otel=<position>`. */
  readonly position: number;
  /** Bilinmiyorsa null; uydurulmaz. */
  readonly yildiz: 3 | 4 | 5 | null;
  /** Otel adı zorunlu değil. Yoksa ekran ve PDF yalnız sınıfı yazar. */
  readonly otelAdi: string | null;
}

/** Otel bağlı olmayan turlarda kalkışın TEK oteli. Bilinmeyen alan null; uydurulmaz. */
export interface SecenekOteli {
  readonly yildiz: 3 | 4 | 5 | null;
  readonly otelAdi: string | null;
}

export interface Kalkis {
  readonly kalkisId: string;
  /** YYYY-MM-DD */
  readonly tarih: string;
  /** Çift kişilik oda, kişi başı. Sınıf başına; fiyat yoksa null (ekranda "fiyat yok" yazar, uydurma yok). */
  readonly sinifFiyatlari: readonly { readonly position: number; readonly fiyat: Para | null }[];
  /** Sitenin tur sayfasındaki bu kalkışın iki kişilik oda kişi başı fiyatı; aynı tarihli kalkışları ayırt eder. Yoksa yoktur. */
  readonly kisiBasi?: Para | null;
  /** Kalkışın tek oteli (otel bağlı olmayan tur). Alan yoksa ya da null ise eski kural işler. */
  readonly secenekOteli?: SecenekOteli | null;
  /** Kalkış düzeyinde çift kişilik oda kişi başı başlangıç fiyatı (otel bağlı olmayan tur). Yoksa ya da null ise eski kural. */
  readonly kalkisFiyati?: Para | null;
}

export interface UcusBilgisi {
  readonly yon: 'GIDIS' | 'DONUS';
  readonly tarih: string;
  readonly sefer: string | null;
  readonly kalkisHavalimani: string | null;
  readonly kalkisSaati: string | null;
  readonly varisHavalimani: string | null;
  readonly varisSaati: string | null;
}

export interface ProgramGunu {
  readonly gun: number;
  readonly baslik: string;
  readonly metin: string;
  readonly konaklama: string | null;
  /** v3'ün editoryal kısa açıklaması (PDF'te gün altında görünür); yoksa PDF gövde metninin ilk cümlesini kullanır. */
  readonly ozet?: string | null;
}

export interface OpsiyonelTur {
  readonly ad: string;
  readonly fiyat: Para | null;
  /** PDF kartının karesi: 'URUN' katalogdaki eşleşen etkinlik ürününün karesi, 'SEHIR' şehir havuzundan kısa künyeli kare. Eşleşme yoksa yok. */
  readonly gorsel?: { readonly adres: string; readonly altMetin: string | null; readonly eslesme: 'URUN' | 'SEHIR'; readonly urunKimligi?: string | null } | null;
}

export interface TurBilgisi extends TurOzeti {
  readonly ustBaslik: string | null;
  readonly aciklama: string | null;
  readonly sayfaAdresi: string;
  /** v3 ürün kimliği (`product.id`, ondalık, başında sıfır yok). Yalnız PDF'teki WhatsApp izleme adresinin `urun` parametresi için; yoksa parametre hiç yazılmaz. */
  readonly urunId?: string | null;
  /** v3'ün kalıcı tur numarasından türeyen kısa adres (örn. https://mbdtravel.com/t/123456). Yoksa null; yoksa sayfa adresi gösterilir. */
  readonly kisaAdres?: string | null;
  readonly kalkislar: readonly Kalkis[];
  readonly otelSiniflari: readonly OtelSinifi[];
  readonly ucuslar: readonly UcusBilgisi[];
  readonly program: readonly ProgramGunu[];
  readonly dahil: readonly string[];
  readonly haric: readonly string[];
  readonly opsiyonelTurlar: readonly OpsiyonelTur[];
  /** Şehir vergisi ve bahşiş notu (rehbere ödenir). */
  readonly vergiNotu: string | null;
  /** `kaynak`: 'TUR' | 'SIMGE' | 'KART' turun kendi/kart karesi, 'SEHIR' şehir havuzundan, 'ULKE' süzülmüş ülke yedeği (`gorsel-yedegi.ts`). */
  readonly gorseller: readonly { readonly adres: string; readonly altMetin: string | null; readonly kaynak?: 'TUR' | 'SIMGE' | 'KART' | 'SEHIR' | 'ULKE' }[];
  readonly sozlesmeAdresi: string;
  /** Fiyatın hesaplandığı an ve kurun tarihi (ISO). */
  readonly fiyatTarihi: string;
  readonly kurTarihi: string | null;
}

export interface YolcuKompozisyonu {
  readonly yetiskin: number;
  /** 2-11 yaş */
  readonly cocuk: number;
  /** 0-2 yaş */
  readonly bebek: number;
}

export interface Secim {
  readonly turNo: TurNo;
  readonly kalkisId: string;
  readonly otelPosition: number;
  readonly yolcular: YolcuKompozisyonu;
  readonly paraBirimi: ParaBirimi;
  readonly dil: Dil;
}

export interface FiyatCevabi {
  /** Sitedeki aynı seçimin fiyatı. Hesaplanamıyorsa null ve `neden` dolu. */
  readonly kisiBasi: Para | null;
  readonly toplam: Para | null;
  /** `tutar` satır TOPLAMIDIR (adet x birim fiyat), kişi başı değil. */
  readonly yolcuDokumu: readonly { readonly etiket: string; readonly adet: number; readonly tutar: Para }[];
  readonly fiyatTarihi: string;
  readonly kurTarihi: string | null;
  readonly neden: string | null;
}

export interface PdfIstegi {
  readonly tip: PdfTipi;
  readonly turNo: TurNo;
  readonly dil: Dil;
  /** Yalnız BROCHURE: başlangıç fiyatının para birimi (varsayılan USD). VARIANT_QUOTE'ta `secim.paraBirimi` geçerli. */
  readonly paraBirimi?: ParaBirimi;
  /** VARIANT_QUOTE için zorunlu, BROCHURE için gönderilmez. */
  readonly secim?: Omit<Secim, 'turNo' | 'dil'>;
  /** Verilirse PDF yalnız bu kalkışları listeler ve matriste gösterir (`Kalkis.kalkisId`). Verilmezse hepsi. Varyantta seçili kalkış her zaman dahil. Boş dizi geçersiz. */
  readonly kalkisIdleri?: readonly string[];
  /** Verilirse PDF yalnız `TurBilgisi.gorseller` içinde bu sırada (0 tabanlı) görselleri kullanır; boş dizi görselsiz kapak. En çok 4 (PDF boyutu sınırı). Verilmezse ilk dört. */
  readonly gorselIndeksleri?: readonly number[];
  readonly leadId?: string;
  readonly quoteRequestId?: string;
}

export interface PdfCevabi {
  readonly paylasimId: string;
  readonly tip: PdfTipi;
  readonly indirmeAdresi: string;
  readonly baytBoyutu: number;
  /** Önbellekten geldiyse true. */
  readonly onbellektenMi: boolean;
  /** İndirme bağlantısının bitişi (ISO). */
  readonly gecerlilikBitisi: string;
  /** WhatsApp sağlayıcısı yokken personelin kendi WhatsApp'ından göndereceği hazır metin. */
  readonly hazirMesaj: string;
}

export interface GonderimIstegi {
  /** "Müşteri telefonda onay verdi" kutusu. false ise sunucu reddeder. */
  readonly musteriOnayiAlindi: boolean;
  readonly alici: string;
}

export type GonderimDurumu =
  | 'SENT'
  | 'NOT_CONFIGURED'
  | 'BLOCKED_CONSENT'
  | 'BLOCKED_WINDOW'
  | 'BLOCKED_MEDIA'
  | 'FAILED';

export interface GonderimCevabi {
  readonly durum: GonderimDurumu;
  /** İnsan okunur neden; sır içermez. SENT dışında dolu. */
  readonly neden: string | null;
  /** WhatsApp gönderilemediyse ekran bu alanla yedek paneli açar. */
  readonly yedekYol: { readonly hazirMesaj: string; readonly indirmeAdresi: string } | null;
}

export interface KanalDurumu {
  readonly eposta: { readonly hazir: boolean; readonly eksikDegiskenler: readonly string[] };
  readonly whatsapp: { readonly hazir: boolean; readonly neden: string | null };
  readonly v3: { readonly hazir: boolean; readonly neden: string | null };
}

/** PDF en çok bu kadar görsel alır (API gorselIndeksleri ArrayMaxSize ile aynı sayı). */
export const EN_COK_PDF_GORSEL = 4;

/** v3 otel sınıfı tanımsız turda (gemi turu gibi tek fiyatlı) API yine bir sınıf numarası ister; v3 o turda sınıfı yok sayıp kalkış fiyatını verir (otel-sinifi-fiyati.ts tourPriceTable). */
export const SINIFSIZ_TUR_POZISYONU = 0;
